import * as React from 'react'
import {
  type Control,
  Controller,
  type ControllerFieldState,
  type ControllerRenderProps,
  type FieldValues,
  type Path,
} from 'react-hook-form'
import {
  Field,
  FieldContent,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSet,
} from './field'
import { Input } from './input'
import { Textarea } from './textarea'
import { cn } from '../utils'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './select'
import { Checkbox } from './checkbox'
import { RadioGroup, RadioGroupItem } from './radio-group'
import { Tooltip, TooltipContent, TooltipTrigger } from './tooltip'
import { useTranslation } from '@clofo/i18n'
import { ResponsibleLawyerCombobox, type UserData } from './responsible-lawyer-combobox'
import { TypeaheadInput } from './typeahead-input'
import type { Matcher } from 'react-day-picker'
import { DatePicker } from './datepicker'
import { DealStatus } from '@clofo/data/types'
import { DealStatusSelect } from './deal-status-select'

// Helper to join IDs for aria-describedby
function joinIds(...ids: (string | null | undefined)[]) {
  return ids.filter(Boolean).join(' ') || undefined
}

function getAriaDescribedBy(
  description: string | undefined,
  fieldState: ControllerFieldState,
  ids: { descriptionId: string; errorId: string },
) {
  return joinIds(
    description ? `${ids.descriptionId}-field-description` : null,
    fieldState.invalid ? `${ids.errorId}-field-error` : null,
  )
}

type CommonClassNames = {
  field?: string
  label?: string
  error?: string
  description?: string
}

type BaseFieldProps<T extends FieldValues> = {
  control: Control<T>
  name: Path<T>
  label: string
  hideLabel?: boolean
  description?: string
  isRequired?: boolean
  className?: CommonClassNames
  errorWithIcon?: boolean
}

type FormFieldProps<T extends FieldValues> = BaseFieldProps<T> & {
  children: (
    field: ControllerRenderProps<T, Path<T>>,
    fieldState: ControllerFieldState,
    ids: { descriptionId: string; errorId: string },
  ) => React.ReactNode
}

function FormField<T extends FieldValues>({
  control,
  name,
  label,
  hideLabel,
  description,
  isRequired,
  className,
  errorWithIcon,
  children,
}: FormFieldProps<T>) {
  const descriptionId = React.useId()
  const errorId = React.useId()

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => (
        <Field className={className?.field} data-invalid={fieldState.invalid}>
          <FieldLabel
            htmlFor={field.name}
            className={cn(className?.label, hideLabel && 'sr-only')}
            isRequired={isRequired}
          >
            {label}
          </FieldLabel>

          {children(field, fieldState, { descriptionId, errorId })}

          {fieldState.invalid && (
            <FieldError
              id={`${errorId}-field-error`}
              className={className?.error}
              errors={[fieldState.error]}
              withIcon={errorWithIcon}
            />
          )}
          {description && (
            <FieldDescription
              id={`${descriptionId}-field-description`}
              className={className?.description}
            >
              {description}
            </FieldDescription>
          )}
        </Field>
      )}
    />
  )
}

/**
 * For documentation guide on the following components,
 * see: [Controlled Components](https://imanage-wiki.atlassian.net/wiki/x/xQG1hQ)
 */

/* ---------------------------------------- */
/*                  INPUT                   */
/* ---------------------------------------- */
type InputProps<T extends FieldValues> = BaseFieldProps<T> & {
  className?: CommonClassNames & { input?: string }
} & Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    'className' | 'name' | 'id' | 'value' | 'onChange' | 'defaultValue'
  >

function FormInput<T extends FieldValues>({
  control,
  name,
  label,
  hideLabel,
  description,
  isRequired,
  className,
  errorWithIcon,
  ...inputProps
}: InputProps<T>) {
  return (
    <FormField
      control={control}
      name={name}
      label={label}
      hideLabel={hideLabel}
      description={description}
      isRequired={isRequired}
      className={className}
      errorWithIcon={errorWithIcon}
    >
      {(field, fieldState, ids) => (
        <Input
          {...inputProps}
          {...field}
          id={field.name}
          className={className?.input}
          aria-required={isRequired}
          aria-invalid={fieldState.invalid}
          aria-describedby={getAriaDescribedBy(description, fieldState, ids)}
        />
      )}
    </FormField>
  )
}

/* ---------------------------------------- */
/*            TYPEAHEAD INPUT               */
/* ---------------------------------------- */
type TypeaheadInputFieldProps<T extends FieldValues> = BaseFieldProps<T> & {
  suggestions: string[]
  className?: CommonClassNames & { input?: string }
} & Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    'className' | 'name' | 'id' | 'value' | 'onChange' | 'defaultValue'
  >

function FormTypeaheadInput<T extends FieldValues>({
  control,
  name,
  label,
  hideLabel,
  description,
  isRequired,
  className,
  suggestions,
  errorWithIcon,
  ...inputProps
}: TypeaheadInputFieldProps<T>) {
  return (
    <FormField
      control={control}
      name={name}
      label={label}
      hideLabel={hideLabel}
      description={description}
      isRequired={isRequired}
      className={className}
      errorWithIcon={errorWithIcon}
    >
      {(field, fieldState, ids) => (
        <TypeaheadInput
          {...inputProps}
          id={field.name}
          value={field.value ?? ''}
          onChange={field.onChange}
          suggestions={suggestions}
          className={className?.input}
          aria-required={isRequired}
          aria-invalid={fieldState.invalid}
          aria-describedby={getAriaDescribedBy(description, fieldState, ids)}
        />
      )}
    </FormField>
  )
}

/* ---------------------------------------- */
/*                 TEXTAREA                 */
/* ---------------------------------------- */
type TextareaProps<T extends FieldValues> = BaseFieldProps<T> & {
  className?: CommonClassNames & { textarea?: string }
} & Omit<
    React.TextareaHTMLAttributes<HTMLTextAreaElement>,
    'className' | 'name' | 'id' | 'value' | 'onChange' | 'defaultValue'
  >

function FormTextarea<T extends FieldValues>({
  control,
  name,
  label,
  hideLabel,
  description,
  isRequired,
  className,
  errorWithIcon,
  ...textareaProps
}: TextareaProps<T>) {
  return (
    <FormField
      control={control}
      name={name}
      label={label}
      hideLabel={hideLabel}
      description={description}
      isRequired={isRequired}
      className={className}
      errorWithIcon={errorWithIcon}
    >
      {(field, fieldState, ids) => (
        <Textarea
          {...textareaProps}
          {...field}
          id={field.name}
          className={className?.textarea}
          aria-invalid={fieldState.invalid}
          aria-describedby={getAriaDescribedBy(description, fieldState, ids)}
        />
      )}
    </FormField>
  )
}

/* ---------------------------------------- */
/*                  SELECT                  */
/* ---------------------------------------- */

type SelectOption = {
  value: string
  label: string
  disabled?: boolean
  testId?: string
}

type SelectProps<T extends FieldValues> = BaseFieldProps<T> & {
  placeholder?: string
  className?: CommonClassNames & { select?: string }
  items: SelectOption[]
  disabled?: boolean
  transformValue?: (value: string) => unknown
}

function FormSelect<T extends FieldValues>({
  control,
  name,
  label,
  hideLabel,
  description,
  isRequired,
  className,
  placeholder,
  items,
  disabled,
  transformValue,
  errorWithIcon,
}: SelectProps<T>) {
  return (
    <FormField
      control={control}
      name={name}
      label={label}
      hideLabel={hideLabel}
      description={description}
      isRequired={isRequired}
      className={className}
      errorWithIcon={errorWithIcon}
    >
      {(field, fieldState, ids) => (
        <Select
          value={field.value?.toString()}
          onValueChange={val => field.onChange(transformValue ? transformValue(val) : val)}
          disabled={disabled}
        >
          <SelectTrigger
            aria-invalid={fieldState.invalid}
            className={cn(className?.select, fieldState.invalid && 'border-destructive')}
            onBlur={field.onBlur}
            id={field.name}
            aria-describedby={getAriaDescribedBy(description, fieldState, ids)}
          >
            <SelectValue placeholder={placeholder} />
          </SelectTrigger>
          <SelectContent>
            {items.map(opt => (
              <SelectItem
                key={opt.value}
                value={opt.value}
                disabled={opt.disabled}
                data-testid={opt.testId}
              >
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </FormField>
  )
}

/* ---------------------------------------- */
/*                 CHECKBOX                 */
/* ---------------------------------------- */

type BaseCheckboxProps<T extends FieldValues> = {
  control: Control<T>
  name: Path<T>
  description?: string
}

type FormCheckboxProps<T extends FieldValues> = BaseCheckboxProps<T> & {
  label: string
  className?: CommonClassNames & { checkbox?: string }
  disabled?: boolean
  tooltip?: string
}

function FormCheckbox<T extends FieldValues>({
  control,
  name,
  label,
  description,
  className,
  disabled,
  tooltip,
}: FormCheckboxProps<T>) {
  const descriptionId = React.useId()
  const errorId = React.useId()

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => {
        const checkboxElement = (
          <Checkbox
            id={field.name}
            checked={field.value}
            onCheckedChange={field.onChange}
            disabled={disabled}
            className={className?.checkbox}
          />
        )

        return (
          <Field
            className={cn('gap-2', className?.field)}
            orientation='horizontal'
            data-invalid={fieldState.invalid}
          >
            {tooltip ? (
              <Tooltip>
                <TooltipTrigger asChild>{checkboxElement}</TooltipTrigger>
                <TooltipContent>{tooltip}</TooltipContent>
              </Tooltip>
            ) : (
              checkboxElement
            )}

            <FieldLabel
              htmlFor={field.name}
              className={cn(disabled ? 'cursor-default' : 'cursor-pointer', className?.label)}
            >
              {label}
            </FieldLabel>

            {fieldState.invalid && (
              <FieldError
                id={`${errorId}-field-error`}
                className={className?.error}
                errors={[fieldState.error]}
              />
            )}
            {description && (
              <FieldDescription
                id={`${descriptionId}-field-description`}
                className={className?.description}
              >
                {description}
              </FieldDescription>
            )}
          </Field>
        )
      }}
    />
  )
}

/* ---------------------------------------- */
/*             CHECKBOX GROUP               */
/* ---------------------------------------- */

type DisableRule = boolean | ((currentValues: string[], optionValue: string) => boolean)

type CheckboxGroupProps<T extends FieldValues> = BaseCheckboxProps<T> & {
  groupLabel: string
  options: { value: string; label: string; disabled?: boolean; tooltip?: string }[]
  className?: CommonClassNames & { checkbox?: string; checkboxGroup?: string; groupLabel?: string }
  disabled?: DisableRule
}

function FormCheckboxGroup<T extends FieldValues>({
  control,
  name,
  groupLabel,
  options,
  className,
  description,
  disabled,
}: CheckboxGroupProps<T>) {
  const descriptionId = React.useId()
  const errorId = React.useId()

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => (
        <FieldSet data-invalid={fieldState.invalid} className={className?.field}>
          <FieldLegend className={className?.groupLabel}>{groupLabel}</FieldLegend>

          <FieldGroup data-slot='checkbox-group' className={className?.checkboxGroup}>
            {options.map(opt => {
              const isChecked = field.value?.includes(opt.value)

              // ------ CONDITONAL DISABLED LOGIC ------
              const isOptionDisabled =
                opt.disabled ||
                (typeof disabled === 'function'
                  ? disabled(field.value ?? [], opt.value)
                  : disabled === true)

              const checkboxElement = (
                <Checkbox
                  id={`${name}-${opt.value}`}
                  checked={isChecked}
                  onCheckedChange={isChecked => {
                    if (isChecked) {
                      field.onChange([...(field.value ?? []), opt.value])
                    } else {
                      field.onChange(field.value.filter((v: string) => v !== opt.value))
                    }
                  }}
                  className={className?.checkbox}
                  aria-invalid={fieldState.invalid}
                  disabled={isOptionDisabled}
                />
              )

              return (
                <Field
                  key={opt.value}
                  className='gap-2'
                  data-invalid={fieldState.invalid}
                  orientation='horizontal'
                >
                  {opt.tooltip ? (
                    <Tooltip>
                      <TooltipTrigger asChild>{checkboxElement}</TooltipTrigger>
                      <TooltipContent>{opt.tooltip}</TooltipContent>
                    </Tooltip>
                  ) : (
                    checkboxElement
                  )}

                  <FieldLabel
                    htmlFor={`${name}-${opt.value}`}
                    className={cn(
                      isOptionDisabled ? 'cursor-default' : 'cursor-pointer',
                      className?.label,
                    )}
                  >
                    {opt.label}
                  </FieldLabel>
                </Field>
              )
            })}
          </FieldGroup>

          {fieldState.invalid && (
            <FieldError
              id={`${errorId}-field-error`}
              className={className?.error}
              errors={[fieldState.error]}
            />
          )}
          {description && (
            <FieldDescription
              id={`${descriptionId}-field-description`}
              className={className?.description}
            >
              {description}
            </FieldDescription>
          )}
        </FieldSet>
      )}
    />
  )
}

/* ---------------------------------------- */
/*              RADIO GROUP                 */
/* ---------------------------------------- */

type RadioGroupOption = {
  value: string
  label: React.ReactNode
  description?: string
  disabled?: boolean
  tooltip?: string
}

type RadioGroupProps<T extends FieldValues> = {
  control: Control<T>
  name: Path<T>
  groupLabel: string
  options: RadioGroupOption[]
  description?: string
  isRequired?: boolean
  className?: CommonClassNames & { radio?: string; radioGroup?: string; groupLabel?: string }
  disabled?: boolean
  /** Transforms the selected string value before storing in form state */
  transformValue?: (value: string) => unknown
  /** Formats the form state value back to a string for display. Required when transformValue is used with non-string form values. */
  formatValue?: (value: unknown) => string
}

function FormRadioGroup<T extends FieldValues>({
  control,
  name,
  groupLabel,
  options,
  className,
  description,
  disabled,
  isRequired,
  transformValue,
  formatValue,
}: RadioGroupProps<T>) {
  const descriptionId = React.useId()
  const errorId = React.useId()

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => {
        const displayValue = formatValue ? formatValue(field.value) : field.value

        return (
          <FieldSet data-invalid={fieldState.invalid} className={className?.field}>
            <FieldLegend className={className?.groupLabel}>
              {groupLabel}
              {isRequired && <span className='text-destructive ml-1'>*</span>}
            </FieldLegend>

            <RadioGroup
              value={displayValue}
              onValueChange={val => field.onChange(transformValue ? transformValue(val) : val)}
              disabled={disabled}
              className={className?.radioGroup}
              aria-required={isRequired}
              aria-invalid={fieldState.invalid}
              aria-describedby={getAriaDescribedBy(description, fieldState, {
                descriptionId,
                errorId,
              })}
            >
              {options.map(opt => {
                const radioElement = (
                  <RadioGroupItem
                    id={`${name}-${opt.value}`}
                    value={opt.value}
                    disabled={opt.disabled}
                    aria-invalid={fieldState.invalid}
                    aria-describedby={opt.description ? `${name}-${opt.value}-desc` : undefined}
                  />
                )

                return (
                  <Field
                    key={opt.value}
                    className={cn('gap-2', className?.radio)}
                    orientation='horizontal'
                  >
                    {opt.tooltip ? (
                      <Tooltip>
                        <TooltipTrigger asChild>{radioElement}</TooltipTrigger>
                        <TooltipContent>{opt.tooltip}</TooltipContent>
                      </Tooltip>
                    ) : (
                      radioElement
                    )}

                    <FieldContent>
                      <FieldLabel htmlFor={`${name}-${opt.value}`} className={className?.label}>
                        {opt.label}
                      </FieldLabel>
                      {opt.description && (
                        <FieldDescription className='text-xs' id={`${name}-${opt.value}-desc`}>
                          {opt.description}
                        </FieldDescription>
                      )}
                    </FieldContent>
                  </Field>
                )
              })}
            </RadioGroup>

            {fieldState.invalid && (
              <FieldError
                id={`${errorId}-field-error`}
                className={className?.error}
                errors={[fieldState.error]}
              />
            )}
            {description && (
              <FieldDescription
                id={`${descriptionId}-field-description`}
                className={className?.description}
              >
                {description}
              </FieldDescription>
            )}
          </FieldSet>
        )
      }}
    />
  )
}

/* ---------------------------------------- */
/*  CONTROLLED RESPONSIBLE LAWYER COMBOBOX  */
/* ---------------------------------------- */

type FormResponsibleLawyerComboboxProps<T extends FieldValues> = {
  control: Control<T>
  name: Path<T>
  label: string
  hideLabel?: boolean
  description?: string
  isRequired?: boolean
  className?: CommonClassNames & { combobox?: string }
  responsibleLawyerOptions: UserData[]
  placeholder?: string
  noItemFoundText?: string
  disabled?: boolean
  isInDialog?: boolean
}

function FormResponsibleLawyerCombobox<T extends FieldValues>({
  control,
  name,
  label,
  hideLabel,
  description,
  isRequired,
  className,
  responsibleLawyerOptions,
  placeholder,
  noItemFoundText,
  disabled = false,
  isInDialog = false,
}: FormResponsibleLawyerComboboxProps<T>) {
  const { t } = useTranslation()

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => (
        <Field className={className?.field} data-invalid={fieldState.invalid}>
          <FieldLabel
            htmlFor={field.name}
            className={cn(className?.label, hideLabel && 'sr-only')}
            isRequired={isRequired}
          >
            {label}
          </FieldLabel>

          <ResponsibleLawyerCombobox
            responsibleLawyerOptions={responsibleLawyerOptions}
            value={field.value ?? null}
            onChange={value => field.onChange(value || null)}
            placeholder={placeholder || t('overview.common.labels.email')}
            noItemFoundText={noItemFoundText || t('placeholders.typeEmail')}
            disabled={disabled}
            className={cn(className?.combobox, fieldState.invalid && 'border-destructive')}
            hasError={fieldState.invalid}
            isRequired={isRequired}
            isInDialog={isInDialog}
          />

          {fieldState.invalid && (
            <FieldError className={className?.error} errors={[fieldState.error]} />
          )}
          {description && (
            <FieldDescription className={className?.description}>{description}</FieldDescription>
          )}
        </Field>
      )}
    />
  )
}

/* ---------------------------------------- */
/*        CONTROLLED NATIVE DATEPICKER      */
/* ---------------------------------------- */
function FormNativeDatepicker<T extends FieldValues>({
  control,
  name,
  label,
  hideLabel,
  description,
  isRequired,
  className,
  errorWithIcon,
  ...inputProps
}: Omit<InputProps<T>, 'type' | 'onClick'>) {
  const mergedClassName = {
    ...className,
    field: cn('relative w-3/10', className?.field),
    input: cn(
      'w-full [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-inner-spin-button]:hidden hover:cursor-pointer',
      className?.input,
    ),
  }

  return (
    <FormField
      control={control}
      name={name}
      label={label}
      hideLabel={hideLabel}
      description={description}
      isRequired={isRequired}
      className={mergedClassName}
      errorWithIcon={errorWithIcon}
    >
      {(field, fieldState, ids) => (
        <Input
          {...inputProps}
          {...field}
          type='date'
          id={field.name}
          value={field.value ?? ''}
          onChange={e => field.onChange(e.target.value === '' ? null : e.target.value)}
          className={mergedClassName.input}
          onClick={e => e.currentTarget.showPicker?.()}
          aria-invalid={fieldState.invalid}
          aria-describedby={getAriaDescribedBy(description, fieldState, ids)}
        />
      )}
    </FormField>
  )
}

/* ---------------------------------------- */
/*        CONTROLLED CUSTOM DATEPICKER      */
/* ---------------------------------------- */

type FormCustomDatepickerProps<T extends FieldValues> = BaseFieldProps<T> & {
  className?: CommonClassNames & { datepicker?: string }
  startMonth?: Date
  endMonth?: Date
  disabled?: Matcher | Matcher[]
}

function FormCustomDatepicker<T extends FieldValues>({
  control,
  name,
  label,
  hideLabel,
  description,
  isRequired,
  className,
  disabled,
  startMonth,
  endMonth,
  errorWithIcon,
}: FormCustomDatepickerProps<T>) {
  return (
    <FormField
      control={control}
      name={name}
      label={label}
      hideLabel={hideLabel}
      description={description}
      isRequired={isRequired}
      className={className}
      errorWithIcon={errorWithIcon}
    >
      {(field, fieldState, ids) => (
        <DatePicker
          disabled={disabled}
          className={className?.datepicker}
          id={field.name}
          value={field.value ? new Date(field.value) : undefined}
          onChange={date => field.onChange(date)}
          startMonth={startMonth}
          endMonth={endMonth}
          ariaInvalid={fieldState.invalid}
          ariaDescribedby={getAriaDescribedBy(description, fieldState, ids)}
        />
      )}
    </FormField>
  )
}

/* ---------------------------------------- */
/*      CONTROLLED DEAL STATUS SELECT       */
/* ---------------------------------------- */

type FormDealStatusSelectProps<T extends FieldValues> = {
  control: Control<T>
  name: Path<T>
  label: string
  statusOptions: DealStatus[] | null
  hideLabel?: boolean
  description?: string
  isRequired?: boolean
  className?: CommonClassNames & { select?: string }
  disabled?: boolean
  statusOrder?: number[]
  placeholder?: string
}

function FormDealStatusSelect<T extends FieldValues>({
  control,
  name,
  label,
  hideLabel,
  description,
  isRequired,
  className,
  placeholder,
  statusOptions,
  statusOrder,
  disabled,
}: FormDealStatusSelectProps<T>) {
  return (
    <FormField
      control={control}
      name={name}
      label={label}
      hideLabel={hideLabel}
      description={description}
      isRequired={isRequired}
      className={className}
    >
      {(field, fieldState, ids) => (
        <DealStatusSelect
          id={field.name}
          statusOptions={statusOptions}
          value={field.value?.toString() ?? null}
          onChange={value => field.onChange(value ? Number(value) : null)}
          placeholder={placeholder}
          statusOrder={statusOrder}
          disabled={disabled}
          className={cn(className?.select)}
          hasError={fieldState.invalid}
          isRequired={isRequired}
          aria-invalid={fieldState.invalid}
          aria-describedby={getAriaDescribedBy(description, fieldState, ids)}
        />
      )}
    </FormField>
  )
}

export {
  FormCheckbox,
  FormCheckboxGroup,
  FormCustomDatepicker,
  FormDealStatusSelect,
  FormField,
  FormInput,
  FormNativeDatepicker,
  FormRadioGroup,
  FormResponsibleLawyerCombobox,
  FormSelect,
  FormTextarea,
  FormTypeaheadInput,
  getAriaDescribedBy,
}
export type { RadioGroupOption }
